/**
 * Animated background-effect catalog - the SINGLE source of truth for the
 * effect ids, their labels and which of them expose the blur slider.
 *
 * Before this file the id list lived in six hand-kept copies (registry
 * `BG_EFFECT_OPTIONS`, the `bgEffect` showIf guard, `CS_FX_VALUES`, the card
 * stack item schema, the scroll gallery panel schema and the renderer's own
 * `BG_EFFECT_TYPES`). Every new effect had to be pasted into all of them and a
 * missed copy failed silently: the option showed up in the dropdown but the
 * value was coerced away by `propValidate`, or the renderer drew nothing.
 * Import from here instead of writing a new literal array.
 *
 * React-free and dependency-free on purpose: both the schema side
 * (`registry.ts`) and the render side (`render/BgEffect.tsx`, which ships as
 * the standalone `@wa/widgets/bg-effect` entry) import it.
 */

export type BgEffectId =
  | "aurora"
  | "mesh"
  | "conic"
  | "gradient"
  | "spotlight"
  | "stars"
  | "grid"
  | "waves"
  | "orbs"
  | "sheen"
  | "pulse"
  | "nebula"
  | "curtain"
  | "bokeh"
  | "dust"
  | "rays"
  | "ripple"
  | "dots"
  | "lines"
  | "hex"
  | "topo"
  | "noise"
  | "blueprint"
  | "edge"
  | "fog"
  | "halftone"
  | "arcs"
  | "circuit"
  | "iso"
  | "rain"
  | "snow"
  | "glints"
  | "scan"
  | "caustics"
  | "marble"
  | "hatch"
  | "sine"
  | "lowpoly"
  | "radar"
  | "smoke"
  | "glass"
  | "terrain"
  | "prism"
  | "vortex"
  | "warp"
  | "droplets"
  | "constellation"
  | "sunset"
  | "ink"
  | "fireflies"
  | "ember"
  | "leaves"
  | "mosaic"
  | "plus"
  | "lightning"
  | "bars"
  | "glitch"
  | "blinds"
  | "neon"
  | "skyline"
  | "strike"
  | "bubbles"
  | "kaleido"
  | "helix"
  | "globe"
  | "gears"
  | "checkers"
  | "matrix"
  | "confetti"
  | "fireworks"
  | "plasma"
  | "lava"
  | "moire"
  | "shatter"
  | "trails"
  | "hologram"
  | "crt"
  | "heartbeat"
  | "wormhole"
  | "galaxy"
  | "atom"
  | "saturn"
  | "hazard"
  | "rebar"
  | "scaffold"
  | "truss"
  | "crane"
  | "pipes"
  | "weld"
  | "containers"
  | "conveyor"
  | "highway"
  | "pallets"
  | "chevron"
  | "barcode"
  | "parquet"
  | "blockwood"
  | "decking"
  | "terrazzo"
  | "flare"
  | "leak"
  | "iridescent"
  | "ribbon"
  | "frame"
  | "dither"
  | "dotwave"
  | "gridbeam"
  | "cubes"
  | "waveform";

/** Display order in every picker. Keep the original nine first so existing
 *  sites keep the muscle memory; new effects are appended calm-first. */
export const BG_EFFECT_IDS: BgEffectId[] = [
  "aurora",
  "mesh",
  "conic",
  "gradient",
  "spotlight",
  "stars",
  "grid",
  "waves",
  "orbs",
  "sheen",
  "pulse",
  "nebula",
  "curtain",
  "bokeh",
  "dust",
  "rays",
  "ripple",
  "dots",
  "lines",
  "hex",
  "topo",
  "noise",
  "blueprint",
  "edge",
  "fog",
  "halftone",
  "arcs",
  "circuit",
  "iso",
  "rain",
  "snow",
  "glints",
  "scan",
  "caustics",
  "marble",
  "hatch",
  "sine",
  "lowpoly",
  "radar",
  "smoke",
  "glass",
  "terrain",
  "prism",
  "vortex",
  "warp",
  "droplets",
  "constellation",
  "sunset",
  "ink",
  "fireflies",
  "ember",
  "leaves",
  "mosaic",
  "plus",
  "lightning",
  "bars",
  "glitch",
  "blinds",
  "neon",
  "skyline",
  "strike",
  "bubbles",
  "kaleido",
  "helix",
  "globe",
  "gears",
  "checkers",
  "matrix",
  "confetti",
  "fireworks",
  "plasma",
  "lava",
  "moire",
  "shatter",
  "trails",
  "hologram",
  "crt",
  "heartbeat",
  "wormhole",
  "galaxy",
  "atom",
  "saturn",
  "hazard",
  "rebar",
  "scaffold",
  "truss",
  "crane",
  "pipes",
  "weld",
  "containers",
  "conveyor",
  "highway",
  "pallets",
  "chevron",
  "barcode",
  "parquet",
  "blockwood",
  "decking",
  "terrazzo",
  "flare",
  "leak",
  "iridescent",
  "ribbon",
  "frame",
  "dither",
  "dotwave",
  "gridbeam",
  "cubes",
  "waveform",
];

export const BG_EFFECT_LABELS: Record<BgEffectId, { et: string; en: string }> = {
  aurora: { et: "Aurora", en: "Aurora" },
  mesh: { et: "Mesh (macOS)", en: "Mesh (macOS)" },
  conic: { et: "Conic (pöörlev)", en: "Conic (rotating)" },
  gradient: { et: "Animeeritud gradient", en: "Animated gradient" },
  spotlight: { et: "Valgusvihud", en: "Light beams" },
  stars: { et: "Tähed ja meteoorid", en: "Stars and meteors" },
  grid: { et: "Retro-võrk (horisont)", en: "Retro grid (horizon)" },
  waves: { et: "Vedelad lained", en: "Liquid waves" },
  orbs: { et: "Hõljuvad kerad", en: "Floating orbs" },
  sheen: { et: "Siidiläige", en: "Silk sheen" },
  pulse: { et: "Hingav sära", en: "Breathing glow" },
  nebula: { et: "Udukogu", en: "Nebula" },
  curtain: { et: "Valguse loorid", en: "Light veils" },
  bokeh: { et: "Bokeh", en: "Bokeh" },
  dust: { et: "Tolmukübemed", en: "Dust motes" },
  rays: { et: "Päikesekiired", en: "Sun rays" },
  ripple: { et: "Lainerõngad", en: "Ripple rings" },
  dots: { et: "Punktivõrk", en: "Dot matrix" },
  lines: { et: "Diagonaaljooned", en: "Diagonal lines" },
  hex: { et: "Kärgvõrk", en: "Honeycomb" },
  topo: { et: "Samakõrgusjooned", en: "Topography" },
  noise: { et: "Filmiterake", en: "Film grain" },
  blueprint: { et: "Sinikoopia", en: "Blueprint grid" },
  edge: { et: "Servavalgus", en: "Edge light" },
  fog: { et: "Hõljuv udu", en: "Drifting fog" },
  halftone: { et: "Rastripunktid", en: "Halftone" },
  arcs: { et: "Orbiidikaared", en: "Orbit arcs" },
  circuit: { et: "Trükkplaat", en: "Circuit traces" },
  iso: { et: "Isomeetriline võrk", en: "Isometric lattice" },
  rain: { et: "Vihm", en: "Rain" },
  snow: { et: "Lumi", en: "Snowfall" },
  glints: { et: "Sädemed", en: "Sparkles" },
  scan: { et: "Skaneerimisjooned", en: "Scanlines" },
  caustics: { et: "Veemustrid", en: "Water caustics" },
  marble: { et: "Marmor", en: "Marble veins" },
  hatch: { et: "Viirutus", en: "Crosshatch" },
  sine: { et: "Lainejooned", en: "Wave lines" },
  lowpoly: { et: "Madalpolügoon", en: "Low poly" },
  radar: { et: "Radar", en: "Radar sweep" },
  smoke: { et: "Suits", en: "Rising smoke" },
  glass: { et: "Klaaspaneelid", en: "Frosted panes" },
  terrain: { et: "Mäekihid", en: "Mountain layers" },
  prism: { et: "Prisma", en: "Prism split" },
  vortex: { et: "Keeris", en: "Vortex" },
  warp: { et: "Hüperruum", en: "Warp streaks" },
  droplets: { et: "Veepiisad", en: "Water droplets" },
  constellation: { et: "Tähtkuju", en: "Constellation" },
  sunset: { et: "Päikeseloojang", en: "Retro sunset" },
  ink: { et: "Tint vees", en: "Ink bloom" },
  fireflies: { et: "Jaanimardikad", en: "Fireflies" },
  ember: { et: "Söed", en: "Embers" },
  leaves: { et: "Langevad lehed", en: "Falling leaves" },
  mosaic: { et: "Mosaiik", en: "Mosaic tiles" },
  plus: { et: "Ristikesed", en: "Plus grid" },
  lightning: { et: "Välk", en: "Lightning" },
  bars: { et: "Ekvalaiser", en: "Sound bars" },
  glitch: { et: "Glitch", en: "Glitch bands" },
  blinds: { et: "Ribikardin", en: "Venetian blinds" },
  neon: { et: "Neoontoru", en: "Neon tubes" },
  skyline: { et: "Linnasiluett", en: "City skyline" },
  strike: { et: "Realistlik välk", en: "Realistic lightning" },
  bubbles: { et: "Mullid", en: "Rising bubbles" },
  kaleido: { et: "Kaleidoskoop", en: "Kaleidoscope" },
  helix: { et: "Spiraalahel", en: "Double helix" },
  globe: { et: "Maakera", en: "Wireframe globe" },
  gears: { et: "Hammasrattad", en: "Turning gears" },
  checkers: { et: "Malelaud", en: "Checker floor" },
  matrix: { et: "Koodivihm", en: "Code rain" },
  confetti: { et: "Konfetid", en: "Confetti" },
  fireworks: { et: "Ilutulestik", en: "Fireworks" },
  plasma: { et: "Plasma", en: "Plasma field" },
  lava: { et: "Laavalamp", en: "Lava lamp" },
  moire: { et: "Muaree", en: "Moire interference" },
  shatter: { et: "Mõrad klaasis", en: "Cracked glass" },
  trails: { et: "Valgusrajad", en: "Light trails" },
  hologram: { et: "Hologramm", en: "Hologram" },
  crt: { et: "Kineskoop", en: "CRT screen" },
  heartbeat: { et: "Pulsijoon", en: "Heartbeat trace" },
  wormhole: { et: "Ussiauk", en: "Wormhole tunnel" },
  galaxy: { et: "Galaktika", en: "Spiral galaxy" },
  atom: { et: "Aatom", en: "Atom orbits" },
  saturn: { et: "Rõngasplaneet", en: "Ringed planet" },
  hazard: { et: "Hoiatustriibud", en: "Hazard stripes" },
  rebar: { et: "Sarrusevõrk", en: "Rebar mesh" },
  scaffold: { et: "Tellingud", en: "Scaffolding" },
  truss: { et: "Sõrestik", en: "Steel truss" },
  crane: { et: "Tornkraana", en: "Tower crane" },
  pipes: { et: "Torustik", en: "Pipe network" },
  weld: { et: "Keevitussädemed", en: "Welding sparks" },
  containers: { et: "Konteinerid", en: "Container yard" },
  conveyor: { et: "Konveier", en: "Conveyor belt" },
  highway: { et: "Öine maantee", en: "Night highway" },
  pallets: { et: "Riiulid ja alused", en: "Pallet racking" },
  chevron: { et: "Suunanooled", en: "Chevron flow" },
  barcode: { et: "Triipkood", en: "Barcode scan" },
  parquet: { et: "Kalasabaparkett", en: "Chevron parquet" },
  blockwood: { et: "Mosaiikparkett", en: "Block parquet" },
  decking: { et: "Terrassilaud", en: "Timber decking" },
  terrazzo: { et: "Terratsopõrand", en: "Terrazzo floor" },
  flare: { et: "Objektiivi sähvatus", en: "Lens flare" },
  leak: { et: "Valguselekked", en: "Light leaks" },
  iridescent: { et: "Iriseeruv kroom", en: "Iridescent chrome" },
  ribbon: { et: "Siidilint", en: "Silk ribbon" },
  frame: { et: "Raamikiir", en: "Border beam" },
  dither: { et: "Ditter-gradient", en: "Dithered gradient" },
  dotwave: { et: "Punktilaine", en: "Dot wave" },
  gridbeam: { et: "Võrgukiired", en: "Grid beams" },
  cubes: { et: "Hõljuvad kuubikud", en: "Floating cubes" },
  waveform: { et: "Lainevirn", en: "Waveform stack" },
};

/** Effects whose look actually depends on `--wa-bge-blur`; only these show the
 *  blur slider. The rest read the variable through a `calc()` factor or ignore
 *  it entirely, so exposing the control would be a setting with no reader. */
export const BG_EFFECT_BLUR_IDS: BgEffectId[] = ["aurora", "mesh", "nebula", "curtain", "bokeh", "pulse", "fog", "smoke", "ink", "strike", "kaleido", "plasma", "lava", "weld", "leak", "iridescent"];

/** Membership test shared by the renderer and by the per-card / per-panel
 *  media pickers that reuse the same effect layer. */
export const BG_EFFECT_ID_SET: ReadonlySet<string> = new Set<string>(BG_EFFECT_IDS);

export const isBgEffectId = (v: unknown): v is BgEffectId => typeof v === "string" && BG_EFFECT_ID_SET.has(v);

/**
 * Effect ids offered inside a picker that ALREADY has its own "gradient"
 * option (the scroll gallery panel background). Listing `gradient` twice would
 * put two different meanings behind one value.
 */
export const BG_EFFECT_IDS_NO_GRADIENT: BgEffectId[] = BG_EFFECT_IDS.filter((id) => id !== "gradient");

/**
 * The picker families (`fields` / `patterns` / `particles` / `scenes` /
 * `cosmos` / `screens`) live in `./bgEffectGroups.ts` and are re-exported here,
 * so a picker imports the ids, the labels and the grouping from ONE module.
 */
export { BG_EFFECT_GROUPS, BG_EFFECT_GROUP_OF, type BgEffectGroupId } from "./bgEffectGroups.js";

/** Which of these effects still read on a BUTTON. Lives in `./buttonFxIds.ts`
 *  (AGENTS.md §4) because the reasoning behind the list is a page long and
 *  grows with the catalog; re-exported here so a caller imports one module. */
export { BUTTON_FX_IDS } from "./buttonFxIds.js";

/** `{ value, label }` rows for an inspector `select` field. */
export const bgEffectOptions = (ids: BgEffectId[] = BG_EFFECT_IDS): { value: string; label: { et: string; en: string } }[] =>
  ids.map((id) => ({ value: id, label: BG_EFFECT_LABELS[id] }));
